import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import Dropzone from "react-dropzone";
import classnames from "classnames";
import { RiDeleteBin6Line } from "react-icons/ri";
import { SketchPicker } from 'react-color';

import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../helpers/axiosInstance";

const EcommerceAddProduct = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    discount: 0,
    category: "",
    stock: "",
    ageGroup: "",
    brand: "",
    images: [],
    volumetricWeight: "",
    hsn: "",
    tax: "",
    colors: [],
  });

  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Add Product", link: "#" },
  ]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/brands");
      setBrands(response.data.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    } 
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axiosInstance.get("api/v1/categories");
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      alert("Failed to load categories. Please refresh the page.");
    } finally {
      setLoadingCategories(false);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadImageToOm = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const handleAcceptedFiles = async (files) => {
    setUploading(true);
    try {
      // Add preview to files
      const filesWithPreview = files.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          htmlFormattedSize: formatBytes(file.size),
        })
      );
      setSelectedFiles((prev) => [...prev, ...filesWithPreview]);

      // Upload each file and get URLs
      const uploadPromises = files.map((file) => uploadImageToOm(file));
      const uploadedUrls = await Promise.all(uploadPromises);

      // Add new URLs to existing ones
      setUploadedImages((prev) => [...prev, ...uploadedUrls]);
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
      }));
    } catch (error) {
      alert("Failed to upload one or more images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.images.length === 0) {
      alert("Please upload at least one product image");
      return;
    }

    try {
      const response = await axiosInstance.post(
        "api/v1/admin/product/new",
        formData
      );

      if (response.data.success) {
        alert("Product created successfully!");
        // Reset form
        setFormData({
          name: "",
          description: "",
          price: "",
          discount: 0,
          category: "",
          stock: "",
          ageGroup: "",
          brand: "",
          images: [],
          colors: [],
          volumetricWeight: "",
          hsn: "",
          tax: "",
        });
        setSelectedFiles([]);
        setUploadedImages([]);
        setActiveTab(1);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Error creating product");
    }
  };

  const ageGroups = [
    "0-2 years",
    "2-4 years",
    "4-6 years",
    "6-8 years",
    "8-10 years",
    "10+ years",
  ];

  const handleColorChange = (color) => {
    setCurrentColor(color.hex);
  };

  const addColor = () => {
    setFormData(prev => ({
      ...prev,
      colors: [...prev.colors, currentColor]
    }));
    setShowColorPicker(false);
    setCurrentColor('#000000');
  };

  const removeColor = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter((_, index) => index !== indexToRemove)
    }));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Add Product" breadcrumbItems={breadcrumbItems} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div
                    id="addproduct-nav-pills-wizard"
                    className="twitter-bs-wizard"
                  >
                    <Nav className="twitter-bs-wizard-nav nav nav-pills nav-justified">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 1 })}
                          onClick={() => toggleTab(1)}
                        >
                          <span className="step-number">01</span>
                          <span className="step-title">Basic Info</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 2 })}
                          onClick={() => toggleTab(2)}
                        >
                          <span className="step-number">02</span>
                          <span className="step-title">Product Images</span>
                        </NavLink>
                      </NavItem>
                    </Nav>

                    <TabContent
                      activeTab={activeTab}
                      className="twitter-bs-wizard-tab-content"
                    >
                      <TabPane tabId={1}>
                        <CardTitle className="h5">Basic Information</CardTitle>
                        <p className="card-title-desc">
                          Fill all information below
                        </p>
                        <Form>
                          <div className="mb-3">
                            <Label className="form-label" htmlFor="name">
                              Product Name *
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              className="form-control"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <Label className="form-label" htmlFor="description">
                              Product Description *
                            </Label>
                            <textarea
                              className="form-control"
                              id="description"
                              name="description"
                              rows="5"
                              value={formData.description}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <Row>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label className="form-label" htmlFor="price">
                                  Price *
                                </Label>
                                <Input
                                  id="price"
                                  name="price"
                                  type="number"
                                  className="form-control"
                                  value={formData.price}
                                  onChange={handleInputChange}
                                  required
                                  min="0"
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="discount"
                                >
                                  Discount (%)
                                </Label>
                                <Input
                                  id="discount"
                                  name="discount"
                                  type="number"
                                  className="form-control"
                                  value={formData.discount}
                                  onChange={handleInputChange}
                                  min="0"
                                  max="100"
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label className="form-label" htmlFor="stock">
                                  Stock *
                                </Label>
                                <Input
                                  id="stock"
                                  name="stock"
                                  type="number"
                                  className="form-control"
                                  value={formData.stock}
                                  onChange={handleInputChange}
                                  required
                                  min="0"
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label className="form-label" htmlFor="brand">
                                  Brand
                                </Label>
                                <select className="form-control" id="brand" name="brand" value={formData.brand} onChange={handleInputChange} required>
                                  <option value="">Select Brand</option>
                                  {brands.map((brand) => (
                                    <option key={brand._id} value={brand._id}>
                                      {brand.title}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="category"
                                >
                                  Category *
                                </Label>
                                {loadingCategories ? (
                                  <div className="text-center">
                                    <Spinner size="sm" /> Loading categories...
                                  </div>
                                ) : (
                                  <select
                                    className="form-control"
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    required
                                  >
                                    <option value="">Select Category</option>
                                    {categories.map((cat) => (
                                      <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="ageGroup"
                                >
                                  Age Group *
                                </Label>
                                <select
                                  className="form-control"
                                  id="ageGroup"
                                  name="ageGroup"
                                  value={formData.ageGroup}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="">Select Age Group</option>
                                  {ageGroups.map((age, index) => (
                                    <option key={index} value={age}>
                                      {age}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="volumetricWeight"
                                >
                                  Volumetric Weight (in kg)
                                </Label>
                                <Input
                                  id="volumetricWeight"
                                  name="volumetricWeight"
                                  type="number"
                                  className="form-control"
                                  value={formData.volumetricWeight}
                                  onChange={handleInputChange}
                                  min="0"
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label className="form-label" htmlFor="hsn">
                                  HSN Code
                                </Label>
                                <Input
                                  id="hsn"
                                  name="hsn"
                                  type="text"
                                  className="form-control"
                                  value={formData.hsn}
                                  onChange={handleInputChange}
                                />
                              </div>
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <Label className="form-label" htmlFor="tax">
                                  Tax (%)
                                </Label>
                                <Input
                                  id="tax"
                                  name="tax"
                                  type="number"
                                  className="form-control"
                                  value={formData.tax}
                                  onChange={handleInputChange}
                                  min="0"
                                />
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={12}>
                              <div className="mb-3">
                                <Label className="form-label">Product Colors</Label>
                                <div className="d-flex flex-wrap gap-2 mb-2">
                                  {formData.colors.map((color, index) => (
                                    <div
                                      key={index}
                                      className="d-flex align-items-center border rounded p-2"
                                      style={{ backgroundColor: color, minWidth: '100px' }}
                                    >
                                      <span className="me-2" style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }}>
                                        {color}
                                      </span>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 ms-auto"
                                        onClick={() => removeColor(index)}
                                      >
                                        <RiDeleteBin6Line style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }} />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-2">
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                  >
                                    {showColorPicker ? 'Close Color Picker' : 'Add Color'}
                                  </Button>
                                  {showColorPicker && (
                                    <div className="mt-2">
                                      <SketchPicker
                                        color={currentColor}
                                        onChange={handleColorChange}
                                      />
                                      <Button
                                        color="primary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={addColor}
                                      >
                                        Add This Color
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      </TabPane>

                      <TabPane tabId={2}>
                        <CardTitle className="h4">Product Images</CardTitle>
                        <p className="card-title-desc">
                          Upload product images *
                        </p>
                        <Form className="dropzone">
                          <Dropzone
                            onDrop={handleAcceptedFiles}
                            accept={{
                              "image/*": [".png", ".jpg", ".jpeg", ".gif"],
                            }}
                            multiple
                            disabled={uploading}
                          >
                            {({ getRootProps, getInputProps }) => (
                              <div className="dropzone">
                                <div
                                  className="dz-message needsclick"
                                  {...getRootProps()}
                                >
                                  <input {...getInputProps()} />
                                  <div className="mb-3">
                                    <i className="display-4 text-muted ri-upload-cloud-2-line" />
                                  </div>
                                  <h4>
                                    {uploading
                                      ? "Uploading images..."
                                      : "Drop files here or click to upload."}
                                  </h4>
                                </div>
                              </div>
                            )}
                          </Dropzone>
                          <div
                            className="dropzone-previews mt-3"
                            id="file-previews"
                          >
                            {selectedFiles.map((f, i) => (
                              <Card
                                className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                key={i + "-file"}
                              >
                                <div className="p-2">
                                  <Row className="align-items-center">
                                    <Col className="col-auto">
                                      <img
                                        data-dz-thumbnail=""
                                        height="80"
                                        className="avatar-sm rounded bg-light"
                                        alt={f.name}
                                        src={f.preview}
                                      />
                                    </Col>
                                    <Col>
                                      <Link
                                        to="#"
                                        className="text-muted font-weight-bold"
                                      >
                                        {f.name}
                                      </Link>
                                      <p className="mb-0">
                                        <strong>{f.htmlFormattedSize}</strong>
                                      </p>
                                    </Col>
                                    <Col className="col-auto">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeImage(i)}
                                      >
                                        <RiDeleteBin6Line />
                                      </Button>
                                    </Col>
                                  </Row>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </Form>

                        <div className="text-center mt-4">
                          <Button
                            type="button"
                            color="primary"
                            className="me-1"
                            onClick={handleSubmit}
                            disabled={uploading || selectedFiles.length === 0}
                          >
                            {uploading ? (
                              <Spinner size="sm" />
                            ) : (
                              "Create Product"
                            )}
                          </Button>
                          <Button type="button" color="light">
                            Cancel
                          </Button>
                        </div>
                      </TabPane>
                    </TabContent>

                    <ul className="pager wizard twitter-bs-wizard-pager-link">
                      <li
                        className={
                          activeTab === 1 ? "previous disabled" : "previous"
                        }
                      >
                        <Link
                          to="#"
                          onClick={() => {
                            toggleTab(activeTab - 1);
                          }}
                        >
                          Previous
                        </Link>
                      </li>
                      {activeTab === 1 && (
                        <li
                          className={activeTab === 2 ? "next disabled" : "next"}
                        >
                          <Link
                            to="#"
                            onClick={() => {
                              toggleTab(activeTab + 1);
                            }}
                          >
                            Next
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EcommerceAddProduct;
